import React from "react";
import { Link } from "react-router-dom";
import { 
  RiFacebookLine, 
  RiInstagramLine, 
  RiLinkedinLine, 
  RiYoutubeLine, 
  RiTwitterXLine 
} from "react-icons/ri";
import { BsEnvelope, BsGeoAlt } from "react-icons/bs";
import { animateScroll as scroll } from "react-scroll";

function Footer() {
  const handleLinkClick = () => {
    scroll.scrollToTop();
  };

  return (
    <footer className="bg-white text-slate-600 border-t border-slate-200/80 font-sans py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main 4-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 pb-12 border-b border-slate-100">
          
          {/* Column 1: Brand & Logo */}
          <div className="lg:col-span-4 space-y-4">
            <Link to="/" onClick={handleLinkClick} className="flex items-center gap-2.5 group inline-block">
              <img 
                src="/logo.png" 
                alt="Beta Communes" 
                className="h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-105" 
              />
              <div className="flex flex-col">
                <span className="text-lg font-extrabold tracking-tight text-slate-900 leading-none">
                  Beta Communes
                </span>
                <span className="text-xs text-slate-500 font-medium tracking-wide mt-0.5">
                  Build · Grow · Together
                </span>
              </div>
            </Link>
          </div>

          {/* Column 2: Quick Links */}
          <div className="lg:col-span-2 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">
              Quick Links
            </h4>
            <ul className="space-y-2 text-xs font-medium text-slate-500">
              <li>
                <Link to="/" onClick={handleLinkClick} className="hover:text-[#f06600] transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/aboutus" onClick={handleLinkClick} className="hover:text-[#f06600] transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/services" onClick={handleLinkClick} className="hover:text-[#f06600] transition-colors">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/portfolio" onClick={handleLinkClick} className="hover:text-[#f06600] transition-colors">
                  Work
                </Link>
              </li>
              <li>
                <Link to="/blog" onClick={handleLinkClick} className="hover:text-[#f06600] transition-colors">
                  Insights
                </Link>
              </li>
              <li>
                <Link to="/contactus" onClick={handleLinkClick} className="hover:text-[#f06600] transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Our Services */}
          <div className="lg:col-span-3 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">
              Our Services
            </h4>
            <ul className="space-y-2 text-xs font-medium text-slate-500">
              <li>
                <Link to="/services" onClick={handleLinkClick} className="hover:text-[#f06600] transition-colors">
                  Web Development
                </Link>
              </li>
              <li>
                <Link to="/ai-automation" onClick={handleLinkClick} className="hover:text-[#f06600] transition-colors">
                  AI Solutions
                </Link>
              </li>
              <li>
                <Link to="/digital-marketing" onClick={handleLinkClick} className="hover:text-[#f06600] transition-colors">
                  Digital Marketing
                </Link>
              </li>
              <li>
                <Link to="/software-development" onClick={handleLinkClick} className="hover:text-[#f06600] transition-colors">
                  Web Applications
                </Link>
              </li>
              <li>
                <Link to="/ui-ux-design" onClick={handleLinkClick} className="hover:text-[#f06600] transition-colors">
                  UI/UX Design
                </Link>
              </li>
              <li>
                <Link to="/services" onClick={handleLinkClick} className="hover:text-[#f06600] transition-colors">
                  Maintenance &amp; Support
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Follow Us & Contact */}
          <div className="lg:col-span-3 space-y-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">
              Follow Us
            </h4>

            {/* Social Icons Strip */}
            <div className="flex items-center gap-2">
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noreferrer" 
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-[#f06600] text-slate-600 hover:text-white flex items-center justify-center text-sm transition-all"
                aria-label="Facebook"
              >
                <RiFacebookLine />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noreferrer" 
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-[#f06600] text-slate-600 hover:text-white flex items-center justify-center text-sm transition-all"
                aria-label="Instagram"
              >
                <RiInstagramLine />
              </a>
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noreferrer" 
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-[#f06600] text-slate-600 hover:text-white flex items-center justify-center text-sm transition-all"
                aria-label="LinkedIn"
              >
                <RiLinkedinLine />
              </a>
              <a 
                href="https://youtube.com" 
                target="_blank" 
                rel="noreferrer" 
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-[#f06600] text-slate-600 hover:text-white flex items-center justify-center text-sm transition-all"
                aria-label="YouTube"
              >
                <RiYoutubeLine />
              </a>
              <a 
                href="https://x.com" 
                target="_blank" 
                rel="noreferrer" 
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-[#f06600] text-slate-600 hover:text-white flex items-center justify-center text-sm transition-all"
                aria-label="Twitter X"
              >
                <RiTwitterXLine />
              </a>
            </div>

            {/* Email & Location */}
            <div className="space-y-2 pt-1 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <BsEnvelope className="text-[#f06600] text-sm" />
                <a href="mailto:support@betacommunes.com" className="hover:text-[#f06600] transition-colors">
                  support@betacommunes.com
                </a>
              </div>
              <div className="flex items-center gap-2">
                <BsGeoAlt className="text-[#f06600] text-sm" />
                <span>Pakistan</span>
              </div>
            </div>

          </div>

        </div>

        {/* Sub-Footer Bar */}
        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-3">
          <div>
            © 2025 Beta Communes. All rights reserved
          </div>
          <div>
            Build a Better Digital Tomorrow.
          </div>
        </div>

      </div>
    </footer>
  );
}

export default Footer;
